// dates.js — normalización de fechas tecleadas a ISO (YYYY-MM-DD).
//
// Los modales de socio usan un input de texto enmascarado (DD/MM/AAAA), no un
// <input type="date">, así que el usuario puede salir del campo a medio escribir.
// La conversión anterior partía por '/' y montaba `${y}-${m}-${d}` SIN validar nada:
// quien tecleaba `01/01/199` y tabulaba generaba **'199-01-01'**, que el backend
// rechazaba. Eso producía tres issues distintos en Sentry producción (birth_date,
// enrollment_date y expiration_date) y, en el PUT, un 500 opaco.
//
// Aquí la regla es explícita: si no es una fecha REAL y completa, no se inventa.

/** Centinela de "no se pudo interpretar". Los llamantes lo usan para decidir si mandan el campo. */
export const DATE_FALLBACK = '2000-01-01';

/**
 * ¿Son año/mes/día una fecha que existe de verdad?
 * Descarta 31/02, meses 13, y —lo que provocaba el bug— años que no son de 4 cifras.
 */
export function isRealDate(year, month, day) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  if (year < 1000 || year > 9999) return false;
  const dt = new Date(Date.UTC(year, month - 1, day));
  return (
    dt.getUTCFullYear() === year &&
    dt.getUTCMonth() === month - 1 &&
    dt.getUTCDate() === day
  );
}

/**
 * Convierte lo tecleado a ISO `YYYY-MM-DD`.
 * Devuelve `null` si está vacío o no se puede interpretar — el llamante decide
 * si eso es un error de usuario o si aplica un valor por defecto.
 */
export function toIsoDate(input) {
  if (input == null) return null;
  const s = String(input).trim();
  if (s === '') return null;

  // Ya viene en ISO: validar que además sea una fecha real (2026-02-31 no lo es).
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const [, y, m, d] = iso;
    return isRealDate(Number(y), Number(m), Number(d)) ? s : null;
  }

  // Formato del input enmascarado: DD/MM/AAAA. El año DEBE tener 4 dígitos.
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const [, d, m, y] = slash;
    if (!isRealDate(Number(y), Number(m), Number(d))) return null;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // Cualquier otra cosa con '/' (incompleta, año corto…) NO se interpreta.
  if (s.includes('/')) return null;

  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString().split('T')[0];
}

/**
 * Igual que `toIsoDate` pero devolviendo el centinela en vez de `null`, para los
 * llamantes que ya razonan comparando contra `DATE_FALLBACK`.
 */
export function toIsoDateOrFallback(input) {
  return toIsoDate(input) ?? DATE_FALLBACK;
}

/**
 * ¿El usuario escribió algo que NO es una fecha válida?
 * Un campo vacío no es un error (es opcional); uno a medio escribir sí.
 */
export function isIncompleteDate(input) {
  if (input == null) return false;
  const s = String(input).trim();
  if (s === '') return false;
  return toIsoDate(s) === null;
}
